import { useCallback, useMemo, useState, useEffect } from "react";
import { Collapse, Button, Input, Drawer, Badge, Radio } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { FilterOutlined, CheckOutlined } from "@ant-design/icons";

import { AppState } from "store";
import { cancelIcon, chevronClose, chevronOpen, seperatorIcon } from "svgs";
import { Checkbox, DatePicker, SwitchButton } from "components/base";
import {
  onChangeIssuesFilters,
  onChangeRootIssuesFilters,
  setClientListType,
} from "store/actions";
import { getAllCheckedStatus, getIndeterminate } from "utils/helpers";
import { CheckboxPanelList } from "./sub-components";
import { ClientsState } from "../../types";
import "styles/components/tab-filters/client-filter.scss";


const { Panel } = Collapse;
const { RangePicker } = DatePicker;

export default function ClientsFilter() {
  const dispatch = useDispatch();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const [dateRange, setDateRange] = useState<[any, any] | null>(null);
  const [listType, setListType] = useState<string>("current");

  const { filters } = useSelector<AppState, AppState["issues"]>(
    (state) => state.issues
  );

  const { "Time Range": timeRangeUpdated, ...restUpdated } = filters.updated;
  const { "Time Range": timeRangeOriginal, ...restOriginal } = filters.original;

  const isAllChecked = useMemo(() => {
    return getAllCheckedStatus(restUpdated, restOriginal);
  }, [restUpdated, restOriginal]);

  const isIndeterminate = useMemo(() => {
    return getIndeterminate(restUpdated, restOriginal);
  }, [restUpdated, restOriginal]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    Object.entries(restUpdated).forEach(([key, filterArray]: [string, any[]]) => {
      count += filterArray.length;
    });
    if (dateRange && dateRange[0] && dateRange[1]) {
      count += 1;
    }
    return count;
  }, [restUpdated, dateRange]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
      if (window.innerWidth >= 992) {
        setMobileDrawerOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const originalClients = [
    { label: "Foodpanda", value: "Foodpanda" },
    { label: "TCS", value: "TCS" },
    { label: "Leopord Courier", value: "Leopord Courier" },
    { label: "Bike 4 Everything", value: "Bike 4 Everything" },
    { label: "M&P Courier", value: "M&P Courier" },
    { label: "Bykea", value: "Bykea" },
    { label: "Careem", value: "Careem" },
    { label: "Uber", value: "Uber" },
    { label: "Albaik", value: "Albaik" },
    { label: "Flipkart", value: "Flipkart" },
    { label: "eBikeGO", value: "eBikeGO" },
    { label: "Zomato", value: "Zomato" },
    { label: "Rapido", value: "Rapido" },
    { label: "Deliveroo", value: "Deliveroo" },
    { label: "Pizza Hut", value: "Pizza Hut" },
    { label: "Talabat", value: "Talabat" },
  ];

  const onCheckboxListChange = useCallback(
    (name, value, type) => {
      dispatch(onChangeIssuesFilters(name, value, type));
    },
    [dispatch]
  );

  const onRootChange = useCallback(
    (event) => {
      dispatch(onChangeRootIssuesFilters(event.target.checked));
    },
    [dispatch]
  );

  const onClearAll = useCallback(() => {
    dispatch(onChangeRootIssuesFilters(false));
    setDateRange(null);
  }, [dispatch]);

  const onListTypeChange = useCallback(
    (value: string) => {
      setListType(value);
      dispatch(setClientListType(value as ClientsState["filters"]["listType"]));
    },
    [dispatch]
  );

  const onDateRangeChange = useCallback((dates: any) => {
    setDateRange(dates);
  }, []);

  const handleOpenDrawer = useCallback(() => {
    setMobileDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setMobileDrawerOpen(false);
  }, []);

  const handleApplyFilters = useCallback(() => {
    setMobileDrawerOpen(false);
  }, []);

const ListTypeSwitch = () => (
  <div className="custom-list-type-switch">
    <Radio.Group
      value={listType}
      onChange={(e) => onListTypeChange(e.target.value)}
      buttonStyle="solid"
      className="list-type-radio-group"
      style={{
        display: 'flex',
        width: '100%',
      }}
    >
      <Radio.Button 
        value="current"
        style={{
          flex: 1,
          textAlign: 'center',
          borderColor: listType === 'current' ? '#F9C534' : '#abababff',
          backgroundColor: listType === 'current' ? '#F9C534' : '#fff',
          color: listType === 'current' ? '#000000' : '#000',
        }}
      >
        Current
      </Radio.Button>
      <Radio.Button 
        value="archived"
        style={{
          flex: 1,
          textAlign: 'center',
          borderColor: listType === 'archived' ? '#F9C534' :'#abababff',
          backgroundColor: listType === 'archived' ? '#F9C534' : '#fff',
          color: listType === 'archived' ? '#000000' : '#000',
        }}
      >
        Archived
      </Radio.Button>
    </Radio.Group>
  </div>
);

  const filterContent = (
    <Collapse
      className="__common-collapse --issues"
      ghost
      expandIcon={({ isActive }) => (isActive ? chevronOpen : chevronClose)}
      expandIconPosition="right"
      defaultActiveKey={["3"]}
    >
      <Panel header="Onboarding Date" key="3" className="">
        <RangePicker
          value={dateRange}
          className="issues-range-picker"
          onChange={onDateRangeChange}
          dropdownClassName="issues-range-picker-popup"
          placeholder={["Start", "End"]}
          separator={seperatorIcon}
          format="MMM DD"
        />
      </Panel>
    </Collapse>
  );

  return (
    <>
      {/* Desktop Filter Sidebar Content */}
      <div className="common-filter clients-filter-desktop">
        <div className="__filter-header">
          <div className="all-options">
            <div className="filter-title-wrapper">
              {/* <Checkbox
                customClasses="--filter-light"
                onChange={onRootChange}
                checked={isAllChecked}
                indeterminate={isIndeterminate}
              > */}
                <span className="filter-text">Filters</span>
              {/* </Checkbox> */}
              {activeFiltersCount > 0 && (
                <Badge count={activeFiltersCount} className="filter-badge" />
              )}
            </div>
            {/* <Button
              className="clear-all-btn"
              icon={cancelIcon}
              type="link"
              danger
              onClick={onClearAll}
            >
              Clear All
            </Button> */}
          </div>
          <ListTypeSwitch />
        </div>
        {filterContent}
      </div>

      {/* Mobile Floating Action Button */}
      {isMobile && (
        <div className="mobile-filter-trigger">
          <Button
            type="primary"
            icon={<FilterOutlined />}
            onClick={handleOpenDrawer}
            className="filter-fab-btn"
          >
            <span className="fab-text">Filters</span>
            {activeFiltersCount > 0 && (
              <span className="fab-badge">{activeFiltersCount}</span>
            )}
          </Button>
        </div>
      )}

      {/* Mobile Filter Drawer */}
      <Drawer
        title={
          <div className="drawer-title-container">
            <div className="drawer-title-wrapper">
              <span className="drawer-title-text">Filters</span>
              {activeFiltersCount > 0 && (
                <Badge count={activeFiltersCount} className="title-badge" />
              )}
            </div>
            <div className="drawer-switch-wrapper">
              <ListTypeSwitch />
            </div>
          </div>
        }
        placement="right"
        onClose={handleCloseDrawer}
        visible={mobileDrawerOpen}
        className="clients-filter-drawer"
        width={isMobile ? "85%" : 400}
        bodyStyle={{ padding: 0 }}
        footer={
          <div className="drawer-footer-container">
            <div className="drawer-footer">
              {/* <Button onClick={onClearAll} className="clear-btn">
                Clear All
              </Button> */}
              <Button
                type="primary"
                onClick={handleApplyFilters}
                icon={<CheckOutlined />}
                className="apply-btn"
              >
                Apply Filters
              </Button>
            </div>
          </div>
        }
      >
        <div className="drawer-filter-content">{filterContent}</div>
      </Drawer>
    </>
  );
}